import {ChangeEvent, useEffect, useState} from 'react';
import './entity-form.scss';
import {useHistory} from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {Alert, Avatar, Box, Button, Divider, IconButton} from '@mui/material';
import {Business, OpenInNew, Save, Delete} from '@mui/icons-material';
import {BsInfoCircle} from 'react-icons/bs';
import {useAuth} from '../../contexts/auth/context';
import {useOffice} from '../../contexts/office/context';
import {useSnackBar} from '../../contexts/snack-bar/context';
import moment from 'moment';
import {RouterPrompt} from '../router-prompt/router-prompt';
import {ContentPage} from '../content-page/content-page';
import {LoadingContentAbsolute} from '../loading-content/loading-content-absolute';
import {ReadOnlyTextField} from '../read-only-text-field/read-only-text-field';
import {PhoneNumber, parsePhoneNumber} from 'libphonenumber-js/max';
import {SubmitButton, SubmitButtonState} from '../submit-button/submit-button';
import {EditPicture} from '../edit-picture/edit-picture';
import {PhoneNumberInput} from '../phone-number-input/phone-number-input';
import {AlertModal} from '../alert-modal/alert-modal';
import {Option} from '../select-dropdown/select-dropdown';
import FlatInput from '../flat-input/flat-input';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';
import {FlatMultiTextSelect} from '../flat-multi-text-select/flat-multi-text-select';
import {BackendLanguageTag, StorageType, StorageFileType, RegionCode} from '../../backend/enums';
import {EntitiesRepository} from '../../repositories/entities';
import {StorageRequestRepository} from '../../repositories/storage-request';
import {Entity} from '../../backend/interfaces/entity';
import {
  EntityPictureStorageRequestData,
  EntityServicesImageStorageRequestData,
  StorageRequestResult,
} from '../../backend/interfaces/storage-request';
import {PostStorageRequest} from '../../backend/requests/post-storage-request';
import {PutEntity} from '../../backend/requests/put-entity';
import {PostEntity} from '../../backend/requests/post-entity';
import {CaregiverMode, ScreenPaths} from '../../enums';
import {
  appSizes,
  EMAIL_REGEX,
  URL_REGEX,
  avatarSizesAndCoefficients,
  FULL_NAME_MAX_CHARACTERS,
  DISPLAY_NAME_MAX_CHARACTERS,
} from '../../utils/constants';
import {DEFAULT_TIME_ZONE, languages, regions} from '../../utils/data';
import {FlatTimeZoneSelect} from '../flat-time-zone-select/flat-time-zone-select';
import {
  convertArrayToIndex,
  getCountryFlagByCDN,
  validateMobilePhoneNumber,
} from '../../utils/functions';
import {Tooltip} from '../tooltip/tooltip';
import {entitySpecialPurposes} from '../../utils/data/entity-special-purposes';
import {useEntity} from '../../contexts/entity/context';
import {FlatSelectDate} from '../flat-select-date/flat-select-date';
import {useEntitySocialResponses} from '../../hooks/use-entity-social-responses';

type PhoneNumberExtension = {
  phone: string;
  code: string;
};

/**
 * Form to view, edit, remove, and create entities.
 *
 * It uses the office context to get the entity's information.
 *
 * Permissions are given by props.
 *
 * Future:
 * - SOS button manipulation
 * - Parent entity manipulation
 */
export const EntityForm = (props: {
  redirectUrl: string;
  showPageTitle: boolean;
  showExitButton: boolean;
  caregiverMode: CaregiverMode;
  canWrite: boolean;
  hasDIaccess: boolean;
}) => {
  // Get context
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  const authContext = useAuth();
  const {updateUserEntitySocialResponses} = useEntity();

  // Get navigation info
  let history = useHistory();

  // Translations
  const {t} = useTranslation();

  const defaultPhoneNumberExtension: PhoneNumberExtension = {
    phone: '+351',
    code: 'PT',
  };

  // Hold state
  const [originalModel, setOriginalModel] = useState<Entity | undefined>();
  const [model, setModel] = useState<Entity | undefined>();
  const [landlineCountryCode, setLandlineCountryCode] = useState<PhoneNumberExtension>(
    defaultPhoneNumberExtension
  );
  const [landlineInternationalPhoneNumber, setLandlineInternationalPhoneNumber] = useState<
    string | undefined
  >();
  const [mobileCountryCode, setMobileCountryCode] = useState<PhoneNumberExtension>(
    defaultPhoneNumberExtension
  );
  const [mobileInternationalPhoneNumber, setMobileInternationalPhoneNumber] = useState<
    string | undefined
  >();
  const [servicesCountryCode, setServicesCountryCode] = useState<PhoneNumberExtension>(
    defaultPhoneNumberExtension
  );
  const [servicesInternationalPhoneNumber, setServicesInternationalPhoneNumber] = useState<
    string | undefined
  >();
  const [pictureFile, setPictureFile] = useState<File | undefined>();
  const [servicesImageFile, setServicesImageFile] = useState<File | undefined>();

  // Buttons
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState<boolean>(false);

  // Form validation
  const [dataChanged, setDataChanged] = useState<boolean>(false);
  const [nameError, setNameError] = useState<string | undefined>();
  const [showNameError, setShowNameError] = useState<boolean>(false);
  const [shortNameError, setShortNameError] = useState<string | undefined>();
  const [showShortNameError, setShowShortNameError] = useState<boolean>();
  const [socialResponsesError, setSocialResponsesError] = useState<string | undefined>();
  const [showSocialResponsesError, setShowSocialResponsesError] = useState<boolean>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const [showEmailError, setShowEmailError] = useState<boolean>(false);
  const [websiteError, setWebsiteError] = useState<string | undefined>();
  const [showWebsiteError, setShowWebsiteError] = useState<boolean>(false);
  const [populationCapacityError, setPopulationCapacityError] = useState<string | undefined>();
  const [showPopulationCapacityError, setShowPopulationCapacityError] = useState<boolean>(false);
  const [landlinePhoneNumberError, setLandlinePhoneNumberError] = useState<string | undefined>();
  const [showLandlinePhoneNumberError, setShowLandlinePhoneNumberError] = useState<boolean>(false);
  const [mobilePhoneNumberError, setMobilePhoneNumberError] = useState<string | undefined>();
  const [showMobilePhoneNumberError, setShowMobilePhoneNumberError] = useState<boolean>(false);
  const [servicesPhoneNumberError, setServicesPhoneNumberError] = useState<string | undefined>();
  const [showServicesPhoneNumberError, setShowServicesPhoneNumberError] = useState<boolean>(false);

  // Dialogs
  const [deleteDialogOpened, setDeleteDialogOpened] = useState<boolean>(false);

  // Set model when we get one
  useEffect(() => {
    // Set the model
    const init = async () => {
      if (officeContext.state.entityForm) {
        // Init buttons state
        officeContext.setEntityFormSubmitButtonState(SubmitButtonState.Normal);
        officeContext.setEntityFormDeleteButtonState(SubmitButtonState.Normal);

        // Init some state
        setDataChanged(false);
        setPictureFile(undefined);
        setServicesImageFile(undefined);

        // Note: The original model will have the complete phone numbers (ex: "+351910000000").
        // The model will only have the phone numbers without the country calling code.

        // Parse phone numbers
        let parsedLandlinePhoneNumber: PhoneNumber | undefined;
        let parsedMobilePhoneNumber: PhoneNumber | undefined;
        let parsedServicesPhoneNumber: PhoneNumber | undefined;
        if (officeContext.state.entityForm.landlinePhoneNumber) {
          try {
            parsedLandlinePhoneNumber = parsePhoneNumber(
              officeContext.state.entityForm.landlinePhoneNumber
            );
          } catch (error) {
            // Log
            console.debug('🔴 Could not parse the landline phone number in EntityForm.', error);
          }
        }
        if (officeContext.state.entityForm.mobilePhoneNumber) {
          try {
            parsedMobilePhoneNumber = parsePhoneNumber(
              officeContext.state.entityForm.mobilePhoneNumber
            );
          } catch (error) {
            // Log
            console.debug('🔴 Could not parse the mobile phone number in EntityForm.', error);
          }
        }
        if (officeContext.state.entityForm.servicesPhoneNumber) {
          try {
            parsedServicesPhoneNumber = parsePhoneNumber(
              officeContext.state.entityForm.servicesPhoneNumber
            );
          } catch (error) {
            // Log
            console.debug('🔴 Could not parse the services phone number in EntityForm.', error);
          }
        }

        // Model
        const entityModel: Entity = {
          ...officeContext.state.entityForm,
          landlinePhoneNumber:
            parsedLandlinePhoneNumber && parsedLandlinePhoneNumber.isValid()
              ? parsedLandlinePhoneNumber.nationalNumber.toString()
              : undefined,
          mobilePhoneNumber:
            parsedMobilePhoneNumber && parsedMobilePhoneNumber.isValid()
              ? parsedMobilePhoneNumber.nationalNumber.toString()
              : undefined,
          servicesPhoneNumber:
            parsedServicesPhoneNumber && parsedServicesPhoneNumber.isValid()
              ? parsedServicesPhoneNumber.nationalNumber.toString()
              : undefined,
        };

        // Country code and international phone number
        if (parsedLandlinePhoneNumber && parsedLandlinePhoneNumber.isValid()) {
          // Set country code
          setLandlineCountryCode({
            phone: `+${parsedLandlinePhoneNumber.countryCallingCode.toString()}`,
            code: parsedLandlinePhoneNumber.country?.toString() || '',
          });

          // Format the phone number
          if (!props.canWrite) {
            // Set international phone number
            setLandlineInternationalPhoneNumber(parsedLandlinePhoneNumber.formatInternational());
          }
        }
        if (parsedMobilePhoneNumber && parsedMobilePhoneNumber.isValid()) {
          // Set country code
          setMobileCountryCode({
            phone: `+${parsedMobilePhoneNumber.countryCallingCode.toString()}`,
            code: parsedMobilePhoneNumber.country?.toString() || '',
          });

          // Format the phone number
          if (!props.canWrite) {
            // Set international phone number
            setMobileInternationalPhoneNumber(parsedMobilePhoneNumber.formatInternational());
          }
        }
        if (parsedServicesPhoneNumber && parsedServicesPhoneNumber.isValid()) {
          // Set country code
          setServicesCountryCode({
            phone: `+${parsedServicesPhoneNumber.countryCallingCode.toString()}`,
            code: parsedServicesPhoneNumber.country?.toString() || '',
          });

          // Format the phone number
          if (!props.canWrite) {
            // Set international phone number
            setServicesInternationalPhoneNumber(parsedServicesPhoneNumber.formatInternational());
          }
        }

        setModel(entityModel);
        setOriginalModel(officeContext.state.entityForm);
      }
    };

    // Init
    init();

    // Clean up
    return () => {
      // Log
      console.debug('🧹 Entity form button states cleaned in office context.');

      // Clear button states
      officeContext.clearEntityFormSubmitButtonState();
      officeContext.clearEntityFormDeleteButtonState();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [officeContext.state.entityForm]);

  const entitySocialResponseOptions = useEntitySocialResponses({
    entityId: model?.id,
    caregiverMode: props.caregiverMode,
  });

  // Form validation
  useEffect(() => {
    if (model && originalModel) {
      if (props.canWrite) {
        // ----- Fields validation -----
        // Holds validation
        let nameValid: boolean = false;
        let shortNameValid: boolean = false;
        let socialResponsesValid: boolean = false;
        let emailValid: boolean = false;
        let websiteValid: boolean = false;
        let populationCapacityValid: boolean = false;
        let landlinePhoneNumberValid: boolean = false;
        let mobilePhoneNumberValid: boolean = false;
        let servicesPhoneNumberValid: boolean = false;

        // Name validation
        if (model.name && model.name.trim().length > 0) {
          if (model.name.trim().length <= FULL_NAME_MAX_CHARACTERS) {
            // No errors
            if (nameError !== undefined) {
              setNameError(undefined);
            }

            // Valid
            nameValid = true;
          } else {
            // Errors
            if (showNameError) {
              setNameError(
                t(
                  translations.user_form.errors
                    .form_validation_invalid_display_name_maximum_characters,
                  {
                    max: FULL_NAME_MAX_CHARACTERS,
                  }
                )
              );
            }

            // Invalid
            nameValid = false;
          }
        }

        // Short name validation
        if (
          (model.shortName && model.shortName.trim().length <= DISPLAY_NAME_MAX_CHARACTERS) ||
          !model.shortName
        ) {
          // No errors
          if (shortNameError !== undefined) {
            setShortNameError(undefined);
          }
          // Valid
          shortNameValid = true;
        } else {
          // Errors
          if (showShortNameError) {
            setShortNameError(
              t(
                translations.user_form.errors
                  .form_validation_invalid_display_name_maximum_characters,
                {
                  max: DISPLAY_NAME_MAX_CHARACTERS,
                }
              )
            );
          }

          // Invalid
          shortNameValid = false;
        }

        // Social responses validation
        if (model.socialResponses) {
          if (model.socialResponses?.length >= 1) {
            // No errors
            if (socialResponsesError !== undefined) {
              setSocialResponsesError(undefined);
            }
            // Valid
            socialResponsesValid = true;
          } else {
            // Errors
            if (showSocialResponsesError) {
              setSocialResponsesError(
                t(translations.entity_form.errors.form_validation_required_social_responses)
              );
            }

            // Invalid
            socialResponsesValid = false;
          }
        }

        // Email validation
        if (model.email && model.email.trim().length > 0) {
          if (EMAIL_REGEX.test(model.email)) {
            // No errors
            if (emailError !== undefined) {
              setEmailError(undefined);
            }

            // Valid
            emailValid = true;
          } else {
            // Errors
            if (showEmailError) {
              setEmailError(t(translations.entity_form.errors.form_validation_invalid_email));
            }

            // Invalid
            emailValid = false;
          }
        } else {
          // No errors
          if (emailError !== undefined) {
            setEmailError(undefined);
          }

          // Valid
          emailValid = true;
        }

        // Website validation
        if (model.website && model.website.trim().length > 0) {
          if (URL_REGEX.test(model.website)) {
            // No errors
            if (websiteError !== undefined) {
              setWebsiteError(undefined);
            }

            // Valid
            websiteValid = true;
          } else {
            // Errors
            if (showWebsiteError) {
              setWebsiteError(t(translations.entity_form.errors.form_validation_invalid_website));
            }

            // Invalid
            websiteValid = false;
          }
        } else {
          // No errors
          if (websiteError !== undefined) {
            setWebsiteError(undefined);
          }

          // Valid
          websiteValid = true;
        }

        // Population capacity validation
        if (model.populationCapacity !== undefined) {
          if (model.populationCapacity >= 0) {
            // No errors
            if (populationCapacityError !== undefined) {
              setPopulationCapacityError(undefined);
            }

            // Valid
            populationCapacityValid = true;
          } else {
            // Errors
            if (showPopulationCapacityError) {
              setPopulationCapacityError(
                t(translations.entity_form.errors.form_validation_invalid_capacity)
              );
            }

            // Invalid
            populationCapacityValid = false;
          }
        } else {
          // No errors
          if (populationCapacityError !== undefined) {
            setPopulationCapacityError(undefined);
          }

          // Valid
          populationCapacityValid = true;
        }

        // Landline phone number validation
        if (model.landlinePhoneNumber && model.landlinePhoneNumber.trim().length > 0) {
          try {
            // Parse
            const parsedLandlinePhoneNumber: PhoneNumber | undefined = parsePhoneNumber(
              `${landlineCountryCode.phone}${model.landlinePhoneNumber.trim()}`
            );

            if (parsedLandlinePhoneNumber && parsedLandlinePhoneNumber.isValid()) {
              // No errors
              if (landlinePhoneNumberError !== undefined) {
                setLandlinePhoneNumberError(undefined);
              }

              // Valid
              landlinePhoneNumberValid = true;
            } else {
              // Errors
              if (showLandlinePhoneNumberError) {
                setLandlinePhoneNumberError(
                  t(translations.entity_form.errors.form_validation_invalid_landline_phone_number)
                );
              }

              // Invalid
              landlinePhoneNumberValid = false;
            }
          } catch (error) {
            // Errors
            if (showLandlinePhoneNumberError) {
              setLandlinePhoneNumberError(
                t(translations.entity_form.errors.form_validation_invalid_landline_phone_number)
              );
            }

            // Invalid
            landlinePhoneNumberValid = false;
          }
        } else {
          // No errors
          if (landlinePhoneNumberError !== undefined) {
            setLandlinePhoneNumberError(undefined);
          }

          // Valid
          landlinePhoneNumberValid = true;
        }

        // Mobile phone number validation
        if (model.mobilePhoneNumber && model.mobilePhoneNumber.trim().length > 0) {
          // Validate the phone number
          const phoneNumberValidation = validateMobilePhoneNumber(
            `${mobileCountryCode.phone}${model.mobilePhoneNumber.trim()}`
          );

          if (
            phoneNumberValidation.isMobilePhoneNumber === true &&
            phoneNumberValidation.isValidPhoneNumber === true
          ) {
            // No errors
            if (mobilePhoneNumberError !== undefined) {
              setMobilePhoneNumberError(undefined);
            }

            // Valid
            mobilePhoneNumberValid = true;
          } else if (
            phoneNumberValidation.isMobilePhoneNumber === false &&
            phoneNumberValidation.isValidPhoneNumber === true
          ) {
            // Errors
            if (showMobilePhoneNumberError) {
              setMobilePhoneNumberError(
                t(translations.entity_form.errors.form_validation_not_mobile_phone_number)
              );
            }

            // Invalid
            mobilePhoneNumberValid = false;
          } else {
            // Errors
            if (showMobilePhoneNumberError) {
              setMobilePhoneNumberError(
                t(translations.entity_form.errors.form_validation_invalid_mobile_phone_number)
              );
            }

            // Invalid
            mobilePhoneNumberValid = false;
          }
        } else {
          // No errors
          if (mobilePhoneNumberError !== undefined) {
            setMobilePhoneNumberError(undefined);
          }

          // Valid
          mobilePhoneNumberValid = true;
        }

        // Services phone number validation
        if (model.servicesPhoneNumber && model.servicesPhoneNumber.trim().length > 0) {
          try {
            // Parse
            const parsedServicesPhoneNumber: PhoneNumber | undefined = parsePhoneNumber(
              `${servicesCountryCode.phone}${model.servicesPhoneNumber.trim()}`
            );

            if (parsedServicesPhoneNumber && parsedServicesPhoneNumber.isValid()) {
              // No errors
              if (servicesPhoneNumberError !== undefined) {
                setServicesPhoneNumberError(undefined);
              }

              // Valid
              servicesPhoneNumberValid = true;
            } else {
              // Errors
              if (showServicesPhoneNumberError) {
                setServicesPhoneNumberError(
                  t(translations.entity_form.errors.form_validation_invalid_services_phone_number)
                );
              }

              // Invalid
              servicesPhoneNumberValid = false;
            }
          } catch (error) {
            // Errors
            if (showServicesPhoneNumberError) {
              setServicesPhoneNumberError(
                t(translations.entity_form.errors.form_validation_invalid_services_phone_number)
              );
            }

            // Invalid
            servicesPhoneNumberValid = false;
          }
        } else {
          // No errors
          if (servicesPhoneNumberError !== undefined) {
            setServicesPhoneNumberError(undefined);
          }

          // Valid
          servicesPhoneNumberValid = true;
        }
        // ----- Fields validation -----

        // Check if the phone numbers changed
        let landlinePhoneNumberChanged = false;
        let mobilePhoneNumberChanged = false;
        let servicesPhoneNumberChanged = false;
        if (
          (!originalModel.landlinePhoneNumber && model.landlinePhoneNumber) ||
          (originalModel.landlinePhoneNumber && !model.landlinePhoneNumber) ||
          (model.landlinePhoneNumber &&
            originalModel.landlinePhoneNumber &&
            `${landlineCountryCode.phone}${model.landlinePhoneNumber?.trim()}` !==
              originalModel.landlinePhoneNumber?.trim())
        ) {
          landlinePhoneNumberChanged = true;
        }
        if (
          (!originalModel.mobilePhoneNumber && model.mobilePhoneNumber) ||
          (originalModel.mobilePhoneNumber && !model.mobilePhoneNumber) ||
          (model.mobilePhoneNumber &&
            originalModel.mobilePhoneNumber &&
            `${mobileCountryCode.phone}${model.mobilePhoneNumber?.trim()}` !==
              originalModel.mobilePhoneNumber?.trim())
        ) {
          mobilePhoneNumberChanged = true;
        }
        if (
          (!originalModel.servicesPhoneNumber && model.servicesPhoneNumber) ||
          (originalModel.servicesPhoneNumber && !model.servicesPhoneNumber) ||
          (model.servicesPhoneNumber &&
            originalModel.servicesPhoneNumber &&
            `${servicesCountryCode.phone}${model.servicesPhoneNumber?.trim()}` !==
              originalModel.servicesPhoneNumber?.trim())
        ) {
          servicesPhoneNumberChanged = true;
        }

        if (model.id && props.canWrite) {
          // ---> Edit <---

          // ----- Data changed -----
          let dataWasUpdated: boolean = false;

          // Check if the birthday changed
          let birthdayChanged = false;
          if (
            (model.birthday && !originalModel.birthday) ||
            (!model.birthday && originalModel.birthday) ||
            (model.birthday &&
              originalModel.birthday &&
              !moment(model.birthday).isSame(originalModel.birthday, 'day'))
          ) {
            birthdayChanged = true;
          }

          // Check if the data was changed for the router prompt and the buttons validation
          if (
            model.name.trim() !== originalModel.name.trim() ||
            model.description?.trim() !== originalModel.description?.trim() ||
            model.email?.trim() !== originalModel.email?.trim() ||
            model.languageCode !== originalModel.languageCode ||
            model.picture !== originalModel.picture ||
            model.address?.trim() !== originalModel.address?.trim() ||
            model.number?.trim() !== originalModel.number?.trim() ||
            model.region !== originalModel.region ||
            (model.timeZone || DEFAULT_TIME_ZONE) !==
              (originalModel.timeZone || DEFAULT_TIME_ZONE) ||
            model.socialResponses !== originalModel.socialResponses ||
            model.servicesImageUrl !== originalModel.servicesImageUrl ||
            model.servicesName?.trim() !== originalModel.servicesName?.trim() ||
            model.shortName?.trim() !== originalModel.shortName?.trim() ||
            model.website?.trim() !== originalModel.website?.trim() ||
            model.specialPurpose !== originalModel.specialPurpose ||
            model.populationCapacity !== originalModel.populationCapacity ||
            model.type !== originalModel.type ||
            model.isActive !== originalModel.isActive ||
            landlinePhoneNumberChanged ||
            mobilePhoneNumberChanged ||
            servicesPhoneNumberChanged ||
            birthdayChanged
          ) {
            // Set it
            setDataChanged(true);

            // Set
            dataWasUpdated = true;
          } else {
            // Set it
            setDataChanged(false);

            // Set
            dataWasUpdated = false;
          }
          // ----- Data changed -----

          // ----- Submit button validation -----
          if (
            nameValid &&
            shortNameValid &&
            socialResponsesValid &&
            emailValid &&
            websiteValid &&
            populationCapacityValid &&
            landlinePhoneNumberValid &&
            mobilePhoneNumberValid &&
            servicesPhoneNumberValid &&
            dataWasUpdated
          ) {
            // Enable submit button
            setSubmitButtonDisabled(false);
          } else {
            // Disable submit button
            setSubmitButtonDisabled(true);
          }
          // ----- Submit button validation -----
        } else if (props.caregiverMode === CaregiverMode.SystemAdministrator && !model.id) {
          // ---> Create <---

          // ----- Data changed -----
          // Check if the data was changed for the router prompt
          if (
            model.name.trim() ||
            model.birthday ||
            model.description?.trim() ||
            model.email?.trim() ||
            model.languageCode ||
            model.socialResponses ||
            model.picture ||
            model.address?.trim() ||
            model.number?.trim() ||
            model.region ||
            model.servicesImageUrl ||
            model.servicesName?.trim() ||
            model.shortName?.trim() ||
            model.website?.trim() ||
            model.specialPurpose ||
            model.populationCapacity !== undefined ||
            model.type !== originalModel.type ||
            model.isActive !== originalModel.isActive ||
            landlinePhoneNumberChanged ||
            mobilePhoneNumberChanged ||
            servicesPhoneNumberChanged
          ) {
            // Set it
            setDataChanged(true);
          } else {
            // Set it
            setDataChanged(false);
          }
          // ----- Data changed -----

          // ----- Submit button validation -----
          if (
            nameValid &&
            shortNameValid &&
            socialResponsesValid &&
            emailValid &&
            websiteValid &&
            populationCapacityValid &&
            landlinePhoneNumberValid &&
            mobilePhoneNumberValid &&
            servicesPhoneNumberValid
          ) {
            // Enable submit button
            setSubmitButtonDisabled(false);
          } else {
            // Disable submit button
            setSubmitButtonDisabled(true);
          }
          // ----- Submit button validation -----
        } else {
          // Disable submit button
          setSubmitButtonDisabled(true);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    nameError,
    showNameError,
    shortNameError,
    showShortNameError,
    socialResponsesError,
    showSocialResponsesError,
    emailError,
    landlineCountryCode,
    landlinePhoneNumberError,
    mobileCountryCode,
    mobilePhoneNumberError,
    model,
    originalModel,
    populationCapacityError,
    servicesCountryCode,
    servicesPhoneNumberError,
    showEmailError,
    showLandlinePhoneNumberError,
    showMobilePhoneNumberError,
    showPopulationCapacityError,
    showServicesPhoneNumberError,
    showWebsiteError,
    websiteError,
    props.canWrite,
    props.caregiverMode,
  ]);

  // Exit
  const handleExit = () => {
    // Redirect
    history.push(props.redirectUrl);
  };

  // Open the entity's website
  const handleOpenWebsite = (url: string) => {
    // Process the url
    const processedUrl: string =
      url.search('http://') === -1 && url.search('https://') === -1 ? `https://${url}` : url;

    window.open(processedUrl, '_blank')?.focus();
  };

  const handleChangeServicesImageFile = (image?: {base64: string; file: File}) => {
    if (image) {
      // Update the picture and model
      setServicesImageFile(image.file);
      setModel({
        ...model,
        servicesImageUrl: image.base64, // base64
      } as Entity);
    } else {
      // Clear
      setServicesImageFile(undefined);
      setModel({
        ...model,
        servicesImageUrl: undefined,
      } as Entity);
    }
  };
  // Handle delete dialog opened state
  const handleDeleteDialogOpened = (opened: boolean) => {
    // Change dialog state
    setDeleteDialogOpened(opened);
  };

  // Delete the entity
  const handleDeleteEntity = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || props.caregiverMode !== CaregiverMode.SystemAdministrator) {
      return;
    }

    try {
      // Close dialog
      setDeleteDialogOpened(false);

      // Disable submit button
      setSubmitButtonDisabled(true);

      // Update delete button state
      officeContext.setEntityFormDeleteButtonState(SubmitButtonState.Loading);

      // Delete the entity
      if (originalModel.id) {
        await EntitiesRepository.instance.deleteById(originalModel.id);
      } else {
        // Error
        throw new Error('Invalid values to delete the entity.');
      }

      // Wait some time just to make sure the button state was updated
      // so the router prompt won't appear when the history is updated
      setTimeout(() => {
        // Navigate back
        history.replace(
          props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
            ? `${props.redirectUrl}?${ScreenPaths.EntitiesDeletedQuery}=${originalModel?.name}`
            : `${props.redirectUrl}/?${ScreenPaths.EntitiesDeletedQuery}=${originalModel?.name}`
        );
      }, 500);
    } catch (error: any) {
      // Log
      console.debug('🔴 Could not delete the entity.', error);

      // Update delete button state
      officeContext.setEntityFormDeleteButtonState(SubmitButtonState.Error);

      // Enable submit button again
      setSubmitButtonDisabled(false);
    }
  };

  // Submit changes
  const handleSaveChanges = async () => {
    // Stop execution if the model doesn't exist or the user don't have the right permissions
    // It's not expected to fall back here
    if (!model || !originalModel || !props.canWrite) {
      return;
    }

    try {
      // Update submit button state
      officeContext.setEntityFormSubmitButtonState(SubmitButtonState.Loading);

      // Disabled delete button
      setDeleteButtonDisabled(true);

      // Format phone numbers
      let landlinePhoneNumber: string | undefined;
      let mobilePhoneNumber: string | undefined;
      let servicesPhoneNumber: string | undefined;
      if (model.landlinePhoneNumber) {
        try {
          // Parse phone number
          const parsedLandlinePhoneNumber: PhoneNumber | undefined = parsePhoneNumber(
            `${landlineCountryCode.phone}${model.landlinePhoneNumber.trim()}`
          );

          // Check if the phone number is valid
          if (parsedLandlinePhoneNumber && parsedLandlinePhoneNumber.isValid()) {
            // Set it
            landlinePhoneNumber = parsedLandlinePhoneNumber.number.toString();
          } else {
            // Error
            throw new Error('Landline phone number parse failed.');
          }
        } catch (error) {
          // Error
          console.debug('🔴 Could not process the landline phone number.', error);
        }
      }
      if (model.mobilePhoneNumber) {
        try {
          // Parse phone number
          const parsedMobilePhoneNumber: PhoneNumber | undefined = parsePhoneNumber(
            `${mobileCountryCode.phone}${model.mobilePhoneNumber.trim()}`
          );

          // Check if the phone number is valid
          if (parsedMobilePhoneNumber && parsedMobilePhoneNumber.isValid()) {
            // Set it
            mobilePhoneNumber = parsedMobilePhoneNumber.number.toString();
          } else {
            // Error
            throw new Error('Mobile phone number parse failed.');
          }
        } catch (error) {
          // Error
          console.debug('🔴 Could not process the mobile phone number.', error);
        }
      }
      if (model.servicesPhoneNumber) {
        try {
          // Parse phone number
          const parsedServicesPhoneNumber: PhoneNumber | undefined = parsePhoneNumber(
            `${servicesCountryCode.phone}${model.servicesPhoneNumber.trim()}`
          );

          // Check if the phone number is valid
          if (parsedServicesPhoneNumber && parsedServicesPhoneNumber.isValid()) {
            // Set it
            servicesPhoneNumber = parsedServicesPhoneNumber.number.toString();
          } else {
            // Error
            throw new Error('Services phone number parse failed.');
          }
        } catch (error) {
          // Error
          console.debug('🔴 Could not process the services phone number.', error);
        }
      }

      if (model.id && props.canWrite) {
        // ---> Edit <---

        // Entity picture to upload
        let processedPicture: string | null;
        if (pictureFile) {
          // Request body
          const requestBodyEntityPictureFile: PostStorageRequest<EntityPictureStorageRequestData> =
            {
              type: StorageType.EntityPicture,
              fileType: StorageFileType.JPG,
              data: {
                entityId: model.id,
              },
            };

          // Some file was selected to be upload, request upload to the backend
          const storageEntityPictureRequestResult: StorageRequestResult<EntityPictureStorageRequestData> =
            await StorageRequestRepository.instance.create<EntityPictureStorageRequestData>(
              requestBodyEntityPictureFile
            );

          // Upload file
          processedPicture =
            await StorageRequestRepository.instance.upload<EntityPictureStorageRequestData>(
              storageEntityPictureRequestResult,
              pictureFile
            );
        } else {
          processedPicture = model.picture || null;
        }

        // Entity services image to upload
        let processedServicesImageUrl: string | null;
        if (servicesImageFile) {
          // Request body
          const requestBodyEntityServicesImageFile: PostStorageRequest<EntityServicesImageStorageRequestData> =
            {
              type: StorageType.EntityServicesImage,
              fileType: StorageFileType.JPG,
              data: {
                entityId: model.id,
              },
            };

          // Some file was selected to be upload, request upload to the backend
          const storageEntityServicesImageRequestResult: StorageRequestResult<EntityServicesImageStorageRequestData> =
            await StorageRequestRepository.instance.create<EntityServicesImageStorageRequestData>(
              requestBodyEntityServicesImageFile
            );

          // Upload file
          processedServicesImageUrl =
            await StorageRequestRepository.instance.upload<EntityServicesImageStorageRequestData>(
              storageEntityServicesImageRequestResult,
              servicesImageFile
            );
        } else {
          processedServicesImageUrl = model.servicesImageUrl || null;
        }

        // Request body
        // Note: We are ignoring the parentEntityId because this concept is deprecated for now.
        // The sosButtonId is also ignored because we won't use this concept for now.
        const requestBody: PutEntity = {
          name: model.name.trim(),
          product: model.product,
          type: model.type,
          description: model.description?.trim() || null,
          languageCode: model.languageCode || null,
          region: model.region || null,
          timeZone: model.timeZone || DEFAULT_TIME_ZONE,
          number: model.number?.trim() || null,
          shortName: model.shortName?.trim() || null,
          email: model.email?.trim() || null,
          landlinePhoneNumber: landlinePhoneNumber || null,
          mobilePhoneNumber: mobilePhoneNumber || null,
          populationCapacity:
            model.populationCapacity !== undefined ? model.populationCapacity : null,
          birthday: model.birthday || null, // UTC
          address: model.address?.trim() || null,
          website: model.website?.trim() || null,
          socialResponses: model.socialResponses || null,
          servicesName: model.servicesName?.trim() || null,
          servicesPhoneNumber: servicesPhoneNumber || null,
          servicesImageUrl: processedServicesImageUrl,
          picture: processedPicture,
          // Only the type CreateEditRemove can update specialPurpose and isActive
          isActive: model.isActive,
          specialPurpose:
            props.caregiverMode === CaregiverMode.Professional && props.canWrite
              ? undefined
              : model.specialPurpose || null,
        };
        if (model.socialResponses) {
          updateUserEntitySocialResponses(model.socialResponses);
        }

        // Edit entity
        const updatedEntity = await EntitiesRepository.instance.updateById(model.id, requestBody);

        // Update the user session because some data may have changed.
        // If an error occurs, it's not a problem.
        try {
          await authContext.refreshSessionData(
            authContext.state.session?.selectedRelationship?.id,
            authContext.state.session?.selectedAffiliation?.id
          );
        } catch (error) {
          // Error
          console.debug('🔴 Could not refresh session.', error);
        }

        // Reset context
        officeContext.setEntityForm(updatedEntity);

        // Show toast
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.entity_form.success.edit_success_message),
        });

        // Update submit button state
        officeContext.setEntityFormSubmitButtonState(SubmitButtonState.Success);

        // Enable delete button
        setDeleteButtonDisabled(false);
      } else if (!model.id && props.caregiverMode === CaregiverMode.SystemAdministrator) {
        // ---> Create <---

        // Request body
        // Note: We are ignoring the parentEntityId because this concept is deprecated for now.
        // The sosButtonId is also ignored because we won't use this concept for now.
        // Furthermore, we will set the servicesImageUrl and picture only after the entity is created.
        const requestBody: PostEntity = {
          name: model.name.trim(),
          product: model.product,
          isActive: model.isActive,
          type: model.type,
          description: model.description?.trim() || undefined,
          languageCode: model.languageCode || undefined,
          region: model.region || undefined,
          number: model.number?.trim() || undefined,
          shortName: model.shortName?.trim() || undefined,
          email: model.email?.trim() || undefined,
          landlinePhoneNumber: landlinePhoneNumber || undefined,
          mobilePhoneNumber: mobilePhoneNumber || undefined,
          populationCapacity:
            model.populationCapacity !== undefined ? model.populationCapacity : undefined,
          birthday: model.birthday || undefined, // UTC
          address: model.address?.trim() || undefined,
          website: model.website?.trim() || undefined,
          servicesName: model.servicesName?.trim() || undefined,
          servicesPhoneNumber: servicesPhoneNumber || undefined,
          specialPurpose: model.specialPurpose || undefined,
        };

        // Create the entity
        const newEntity = await EntitiesRepository.instance.create(requestBody);

        // Result
        let resultEntity: Entity = {...newEntity};

        // Now we can edit the entity's picture and servicesImageUrl

        // Entity picture to upload
        let uploadedEntityPictureLinkingUrl: string | undefined;
        if (pictureFile) {
          // If an error occurs, we can't fail the whole creation process because the entity was previously created
          try {
            // Request body
            const requestBodyEntityPictureFile: PostStorageRequest<EntityPictureStorageRequestData> =
              {
                type: StorageType.EntityPicture,
                fileType: StorageFileType.JPG,
                data: {
                  entityId: newEntity.id || '',
                },
              };

            // Some file was selected to be upload, request upload to the backend
            const storageEntityPictureRequestResult: StorageRequestResult<EntityPictureStorageRequestData> =
              await StorageRequestRepository.instance.create<EntityPictureStorageRequestData>(
                requestBodyEntityPictureFile
              );

            // Upload file
            uploadedEntityPictureLinkingUrl =
              await StorageRequestRepository.instance.upload<EntityPictureStorageRequestData>(
                storageEntityPictureRequestResult,
                pictureFile
              );
          } catch (error) {
            // Error
            console.debug('🔴 Could not process the entity picture.', error);
          }
        }

        // Entity services image to upload
        let uploadedEntityServicesImageLinkingUrl: string | undefined;
        if (servicesImageFile) {
          // If an error occurs, we can't fail the whole creation process because the entity was previously created
          try {
            // Request body
            const requestBodyEntityServicesImageFile: PostStorageRequest<EntityServicesImageStorageRequestData> =
              {
                type: StorageType.EntityServicesImage,
                fileType: StorageFileType.JPG,
                data: {
                  entityId: newEntity.id || '',
                },
              };

            // Some file was selected to be upload, request upload to the backend
            const storageEntityServicesImageRequestResult: StorageRequestResult<EntityServicesImageStorageRequestData> =
              await StorageRequestRepository.instance.create<EntityServicesImageStorageRequestData>(
                requestBodyEntityServicesImageFile
              );

            // Upload file
            uploadedEntityServicesImageLinkingUrl =
              await StorageRequestRepository.instance.upload<EntityServicesImageStorageRequestData>(
                storageEntityServicesImageRequestResult,
                servicesImageFile
              );
          } catch (error) {
            // Error
            console.debug('🔴 Could not process the entity services image.', error);
          }
        }

        // Update the entity if necessary
        if (uploadedEntityPictureLinkingUrl || uploadedEntityServicesImageLinkingUrl) {
          // If an error occurs, we can't fail the whole creation process because the entity was previously created
          try {
            // Request body
            // Ignore all values except the required ones and the ones we want to update
            const secondRequestBody: PutEntity = {
              name: newEntity.name,
              product: newEntity.product,
              type: newEntity.type,
              isActive: newEntity.isActive,
              servicesImageUrl: uploadedEntityServicesImageLinkingUrl,
              picture: uploadedEntityPictureLinkingUrl,
            };

            // Edit entity
            const updatedEntity = await EntitiesRepository.instance.updateById(
              newEntity.id || '',
              secondRequestBody
            );

            // Update the result
            resultEntity = {...updatedEntity};
          } catch (error) {
            // Error
            console.debug('🔴 Could not update the entity after creating it.', error);
          }
        }

        // Wait some time just to make sure the button state was updated
        // so the router prompt won't appear when the history is updated
        setTimeout(() => {
          // Reset navigation to edit mode
          history.replace(
            props.redirectUrl.charAt(props.redirectUrl.length - 1) === '/'
              ? `${props.redirectUrl}${newEntity.id}`
              : `${props.redirectUrl}/${newEntity.id}`
          );

          // Reset context
          officeContext.setEntityForm(resultEntity);

          // Show toast
          snackBarContext.setOfficeToastNotification({
            severity: 'success',
            message: t(translations.entity_form.success.create_success_message, {
              name: resultEntity.name,
            }),
          });

          // Update submit button state
          officeContext.setEntityFormSubmitButtonState(SubmitButtonState.Success);

          // Enable delete button
          setDeleteButtonDisabled(false);
        }, 500);
      } else {
        // Throw error
        throw new Error('Missing permissions.');
      }
    } catch (error) {
      // Log
      console.debug('🔴 Could not edit/create the entity.', error);

      // Update submit button state
      officeContext.setEntityFormSubmitButtonState(SubmitButtonState.Error);

      // Enable delete button
      setDeleteButtonDisabled(false);
    }
  };

  // Handle change text fields
  const handleChangeTextField = (
    field:
      | 'name'
      | 'number'
      | 'shortName'
      | 'description'
      | 'address'
      | 'email'
      | 'website'
      | 'landlinePhoneNumber'
      | 'mobilePhoneNumber'
      | 'servicesName'
      | 'servicesPhoneNumber',
    value: string | undefined | null
  ) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set it
    setModel({
      ...model,
      [field]: finalValue,
    } as Entity);
  };

  const handleChangePopulationCapacity = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;

    if (value) {
      // Parse value
      const parsed: number = parseInt(value, 10);

      // Set value if possible
      if (!isNaN(parsed)) {
        setModel({
          ...model,
          populationCapacity: parsed,
        } as Entity);
      }
    } else {
      setModel({
        ...model,
        populationCapacity: undefined,
      } as Entity);
    }
  };

  // Handle picture change
  const handleChangePicture = (image?: {base64: string; file: File}) => {
    if (image) {
      // Update the picture and model
      setPictureFile(image.file);
      setModel({
        ...model,
        picture: image.base64, // base64
      } as Entity);
    } else {
      // Clear
      setPictureFile(undefined);
      setModel({
        ...model,
        picture: undefined,
      } as Entity);
    }
  };

  // Strings to use in the form
  let routerDialogTitle: string = '';
  let routerDialogContent: string = '';
  let routerCancelToast: string = '';

  if (props.caregiverMode === CaregiverMode.SystemAdministrator) {
    routerDialogTitle = model?.id
      ? t(translations.entity_form.router_prompt_edit_title, {
          name: originalModel?.shortName || originalModel?.name || '',
        })
      : t(translations.entity_form.router_prompt_create_title, {name: model?.name || ''});
    routerDialogContent = model?.id
      ? t(translations.entity_form.router_prompt_edit_message)
      : t(translations.entity_form.router_prompt_create_message);
    routerCancelToast = model?.id
      ? t(translations.entity_form.edit_cancel_toast, {
          name: originalModel?.shortName || originalModel?.name || '',
        })
      : t(translations.entity_form.create_cancel_toast);
  } else {
    routerDialogTitle = t(translations.entity_form.router_prompt_edit_title, {
      name: originalModel?.shortName || originalModel?.name || '',
    });
    routerDialogContent = t(translations.entity_form.router_prompt_edit_message);
    routerCancelToast = t(translations.entity_form.edit_cancel_toast, {
      name: originalModel?.shortName || originalModel?.name || '',
    });
  }

  return (
    <ContentPage>
      {model ? (
        <form className="entity-form-container">
          <Box className="entity-form-container">
            {props.canWrite ? (
              <EditPicture
                type="entity"
                className="entity-form-edit-picture"
                alt={t(translations.entity_form.picture_input)}
                src={model?.picture || undefined}
                handleChange={handleChangePicture}
                disabled={
                  officeContext.state.entityFormDeleteButtonState === SubmitButtonState.Loading ||
                  officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                }
                aspect={appSizes.profileImageSize.width / appSizes.profileImageSize.height}
                size={`${appSizes.profileImageSize.width}x${appSizes.profileImageSize.height}`}
                alertMessage={t(translations.entity_form.picture_info_message)}
                useFileCropper
                clearable
              />
            ) : (
              <Box className="entity-form-picture">
                <Avatar
                  alt={model.name}
                  src={model.picture}
                  sx={{
                    width: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                    height: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                    borderRadius: `${
                      avatarSizesAndCoefficients.avatarSizes.extraGiant *
                      avatarSizesAndCoefficients.borderRadiusCoefficients.roundedCornersLow
                    }px`,
                  }}
                >
                  <Business
                    sx={{
                      fontSize:
                        avatarSizesAndCoefficients.avatarSizes.extraGiant *
                        avatarSizesAndCoefficients.iconSizeCoefficient,
                    }}
                  />
                </Avatar>
              </Box>
            )}

            <Box>
              {props.canWrite ? (
                <FlatInput
                  label={t(translations.entity_form.name_input)}
                  value={model.name || ''}
                  placeholder={t(translations.entity_form.name_placeholder_input)}
                  onChange={(e) => handleChangeTextField('name', e.currentTarget.value)}
                  onBlur={() => {
                    if (!showNameError) setShowNameError(true);
                  }}
                  disabled={
                    officeContext.state.entityFormDeleteButtonState === SubmitButtonState.Loading ||
                    officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                  }
                  errors={nameError}
                  counter={
                    nameError
                      ? {
                          current: model.name.trim().length,
                          max: FULL_NAME_MAX_CHARACTERS,
                        }
                      : undefined
                  }
                  required
                />
              ) : (
                <ReadOnlyTextField
                  label={t(translations.entity_form.name_input)}
                  value={model.name || t(translations.entity_form.not_specified)}
                  disabledTooltip={t(translations.entity_form.input_disabled_tooltip)}
                />
              )}
            </Box>

            <Box className="entity-form-name-active-cols">
              {props.canWrite ? (
                <>
                  <Box className="entity-form-left">
                    <FlatInput
                      label={t(translations.entity_form.short_name_input)}
                      value={model.shortName || ''}
                      placeholder={t(translations.entity_form.short_name_placeholder_input)}
                      onChange={(e) => handleChangeTextField('shortName', e.currentTarget.value)}
                      disabled={
                        officeContext.state.entityFormDeleteButtonState ===
                          SubmitButtonState.Loading ||
                        officeContext.state.entityFormSubmitButtonState ===
                          SubmitButtonState.Loading
                      }
                      onBlur={() => setShowShortNameError(true)}
                      errors={shortNameError}
                      counter={
                        shortNameError && model.shortName
                          ? {
                              current: model.shortName.trim().length,
                              max: DISPLAY_NAME_MAX_CHARACTERS,
                            }
                          : undefined
                      }
                    />
                    <Alert
                      severity="info"
                      sx={{mt: shortNameError ? '25px' : '2px'}}
                      icon={<BsInfoCircle fontSize="16px" />}
                    >
                      {t(translations.entity_form.alert.short_name_input)}
                    </Alert>
                  </Box>
                  <Box className="entity-form-right">
                    {props.caregiverMode === CaregiverMode.SystemAdministrator ? (
                      <FlatSelectDropdown
                        id="entity-form-state-select"
                        label={t(translations.entity_form.state_input)}
                        value={
                          model.isActive !== null
                            ? {
                                key: String(model.isActive),
                                label: model.isActive
                                  ? t(translations.entity_form.state_value_true)
                                  : t(translations.entity_form.state_value_false),
                              }
                            : undefined
                        }
                        onChange={(option) =>
                          setModel({
                            ...model,
                            isActive: JSON.parse(option.key),
                          })
                        }
                        required={
                          props.caregiverMode === CaregiverMode.SystemAdministrator ? false : true
                        }
                        disabled={
                          officeContext.state.entityFormDeleteButtonState ===
                            SubmitButtonState.Loading ||
                          officeContext.state.entityFormSubmitButtonState ===
                            SubmitButtonState.Loading
                        }
                        options={[
                          {key: 'true', label: t(translations.entity_form.state_value_true)},
                          {key: 'false', label: t(translations.entity_form.state_value_false)},
                        ]}
                        disabledTooltip={
                          props.caregiverMode !== CaregiverMode.SystemAdministrator
                            ? t(translations.entity_form.edit_is_active_not_allowed_tooltip_title)
                            : undefined
                        }
                      />
                    ) : (
                      <ReadOnlyTextField
                        label={t(translations.entity_form.state_input)}
                        value={
                          model.isActive === true
                            ? t(translations.entity_form.state_value_true)
                            : t(translations.entity_form.state_value_false)
                        }
                        disabledTooltip={t(
                          translations.entity_form.edit_is_active_not_allowed_tooltip_title
                        )}
                      />
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <ReadOnlyTextField
                    className="entity-form-left"
                    label={t(translations.entity_form.short_name_input)}
                    value={model.shortName || t(translations.entity_form.not_specified)}
                    disabledTooltip={t(translations.entity_form.input_disabled_tooltip)}
                  />
                  <ReadOnlyTextField
                    className="entity-form-right"
                    label={t(translations.entity_form.state_input)}
                    value={
                      model.isActive === true
                        ? t(translations.entity_form.state_value_true)
                        : t(translations.entity_form.state_value_false)
                    }
                    disabledTooltip={t(translations.entity_form.input_disabled_tooltip)}
                  />
                </>
              )}
            </Box>

            <Box className="entity-form-even-cols">
              {props.canWrite ? (
                <FlatInput
                  type="email"
                  label={t(translations.entity_form.email_input)}
                  placeholder={t(translations.entity_form.email_placeholder_input)}
                  value={model.email || ''}
                  onChange={(e) => handleChangeTextField('email', e.currentTarget.value)}
                  onBlur={() => setShowEmailError(true)}
                  errors={emailError}
                  disabled={
                    officeContext.state.entityFormDeleteButtonState === SubmitButtonState.Loading ||
                    officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                  }
                />
              ) : (
                <ReadOnlyTextField
                  label={t(translations.entity_form.email_input)}
                  value={model.email || t(translations.entity_form.not_specified)}
                />
              )}
            </Box>

            {props.caregiverMode === CaregiverMode.SystemAdministrator && (
              <Box className="entity-form-even-cols">
                <FlatSelectDropdown
                  clearable
                  id="entity-form-special-purpose-select"
                  label={t(translations.entity_form.special_purpose_input)}
                  value={
                    model.specialPurpose !== null
                      ? {
                          key: String(model.specialPurpose),
                          label: model.specialPurpose
                            ? t(translations.entity_special_purposes[model.specialPurpose])
                            : '',
                        }
                      : undefined
                  }
                  onChange={(option) =>
                    setModel({
                      ...model,
                      specialPurpose: option.key,
                    } as Entity)
                  }
                  disabled={
                    officeContext.state.entityFormDeleteButtonState === SubmitButtonState.Loading ||
                    officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                  }
                  options={entitySpecialPurposes.map((obj) => {
                    return {
                      key: obj.type,
                      label: t(obj.translation),
                    };
                  })}
                />
              </Box>
            )}

            <Box className="entity-form-even-cols">
              {props.canWrite ? (
                <FlatInput
                  label={t(translations.entity_form.website_input)}
                  value={model.website || ''}
                  placeholder={t(translations.entity_form.website_placeholder_input)}
                  onChange={(e) => handleChangeTextField('website', e.currentTarget.value)}
                  onBlur={() => setShowWebsiteError(true)}
                  errors={websiteError}
                  disabled={
                    officeContext.state.entityFormDeleteButtonState === SubmitButtonState.Loading ||
                    officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                  }
                />
              ) : (
                <ReadOnlyTextField
                  label={t(translations.entity_form.website_input)}
                  value={model.website || t(translations.entity_form.not_specified)}
                  disabledTooltip={t(translations.entity_form.input_disabled_tooltip)}
                  endExternalAdornment={
                    model.website ? (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenWebsite(model.website as string)}
                      >
                        <OpenInNew />
                      </IconButton>
                    ) : undefined
                  }
                />
              )}
            </Box>

            <Box className="entity-form-even-cols">
              {props.canWrite ? (
                <FlatInput
                  label={t(translations.entity_form.address_input)}
                  value={model.address || ''}
                  placeholder={t(translations.entity_form.address_placeholder_input)}
                  onChange={(e) => handleChangeTextField('address', e.currentTarget.value)}
                  disabled={
                    officeContext.state.entityFormDeleteButtonState === SubmitButtonState.Loading ||
                    officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                  }
                  multiline
                />
              ) : (
                <ReadOnlyTextField
                  label={t(translations.entity_form.address_input)}
                  value={model.address || t(translations.entity_form.not_specified)}
                  disabledTooltip={t(translations.entity_form.input_disabled_tooltip)}
                  multiline
                />
              )}
            </Box>

            <Box className="entity-form-even-cols">
              {props.canWrite ? (
                <>
                  <FlatSelectDropdown
                    label={t(translations.entity_form.language_input)}
                    placeholder={t(translations.entity_form.language_placeholder_input)}
                    value={
                      model.languageCode
                        ? {
                            key: model.languageCode,
                            label: t(
                              translations.languages[model.languageCode as BackendLanguageTag]
                            ),
                          }
                        : undefined
                    }
                    required
                    options={languages.map((language) => {
                      return {key: language.type, label: t(language.translation)};
                    })}
                    onChange={(e) =>
                      setModel({
                        ...model,
                        languageCode: (e.key || undefined) as BackendLanguageTag | undefined,
                      })
                    }
                    disabled={
                      officeContext.state.entityFormDeleteButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                    }
                  />
                  <FlatInput
                    type="number"
                    label={t(translations.entity_form.capacity_input)}
                    placeholder={t(translations.entity_form.capacity_placeholder_input)}
                    value={
                      model.populationCapacity !== undefined
                        ? model.populationCapacity.toString()
                        : ''
                    }
                    inputProps={{
                      min: 0,
                    }}
                    onChange={handleChangePopulationCapacity}
                    onBlur={() => setShowPopulationCapacityError(true)}
                    errors={populationCapacityError}
                    disabled={
                      officeContext.state.entityFormDeleteButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                    }
                  />
                </>
              ) : (
                <>
                  <ReadOnlyTextField
                    label={t(translations.entity_form.language_input)}
                    value={
                      model.languageCode
                        ? t(translations.languages[model.languageCode])
                        : t(translations.entity_form.not_specified)
                    }
                    disabledTooltip={t(translations.entity_form.input_disabled_tooltip)}
                  />
                  <ReadOnlyTextField
                    label={t(translations.entity_form.capacity_input)}
                    value={
                      model.populationCapacity
                        ? model.populationCapacity.toString()
                        : t(translations.entity_form.not_specified)
                    }
                    disabledTooltip={t(translations.entity_form.input_disabled_tooltip)}
                  />
                </>
              )}
            </Box>

            <Box className="entity-form-even-cols">
              <>
                {props.canWrite ? (
                  <FlatSelectDate
                    label={t(translations.entity_form.anniversary_input)}
                    placeholder={t(translations.entity_form.anniversary_placeholder_input)}
                    value={model.birthday}
                    onChange={(date) => {
                      setModel({
                        ...model,
                        birthday: date
                          ? moment(date)
                              .utc()
                              .set({
                                hours: 0,
                                minutes: 0,
                                seconds: 0,
                                milliseconds: 0,
                              })
                              .toISOString()
                          : undefined,
                      } as Entity);
                    }}
                    disabled={
                      officeContext.state.entityFormDeleteButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                    }
                    disableFuture
                    allowClear
                  />
                ) : (
                  <ReadOnlyTextField
                    label={t(translations.entity_form.anniversary_input)}
                    // Convert to UTC to have the actual day, month and year
                    // Otherwise, it will be converted to the local date time
                    value={
                      model.birthday
                        ? moment(model.birthday).utc().format('LL')
                        : t(translations.entity_form.not_specified)
                    }
                    disabledTooltip={t(translations.entity_form.input_disabled_tooltip)}
                  />
                )}
                {props.caregiverMode === CaregiverMode.SystemAdministrator ? (
                  <FlatSelectDropdown
                    label={t(translations.entity_form.region_input)}
                    placeholder={t(translations.entity_form.region_placeholder_input)}
                    required
                    value={
                      model.region
                        ? {
                            key: model.region,
                            label: t(translations.regions[model.region as RegionCode]),
                          }
                        : undefined
                    }
                    options={regions.map((obj) => {
                      return {key: obj.type, label: t(obj.translation)};
                    })}
                    onChange={(e) =>
                      setModel({
                        ...model,
                        region: (e.key || undefined) as RegionCode | undefined,
                      })
                    }
                    disabled={
                      officeContext.state.entityFormDeleteButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                    }
                  />
                ) : (
                  <Box className="entity-form-empty-space" />
                )}
              </>
            </Box>

            {props.caregiverMode === CaregiverMode.SystemAdministrator && model.id && (
              <Box className="entity-form-even-cols">
                <FlatTimeZoneSelect
                  label={t(translations.entity_form.time_zone_input)}
                  placeholder={t(translations.entity_form.time_zone_placeholder_input)}
                  required
                  value={model.timeZone || DEFAULT_TIME_ZONE}
                  onChange={(timeZone) => setModel({...model, timeZone})}
                  disabled={
                    officeContext.state.entityFormDeleteButtonState === SubmitButtonState.Loading ||
                    officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                  }
                />
                <Box className="entity-form-empty-space" />
              </Box>
            )}

            <Box className="entity-form-even-cols">
              {/* The user may have entered the phone numbers without the country code in the old backend.
                        Because of this, we will show the original phone numbers if they could not be parsed. */}
              {props.canWrite ? (
                <>
                  <PhoneNumberInput
                    label={t(translations.entity_form.landline_phone_number_input)}
                    placeholder={t(translations.entity_form.phone_number_placeholder)}
                    countryCode={landlineCountryCode}
                    phoneNumber={model.landlinePhoneNumber || ''}
                    handleChangeCountryCode={(value) => {
                      setLandlineCountryCode(value);
                    }}
                    handleChangePhoneNumber={(value) =>
                      handleChangeTextField('landlinePhoneNumber', value)
                    }
                    disabled={
                      officeContext.state.entityFormDeleteButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                    }
                    handleBlur={() => setShowLandlinePhoneNumberError(true)}
                    error={landlinePhoneNumberError}
                  />
                  <PhoneNumberInput
                    label={t(translations.entity_form.mobile_phone_number_input)}
                    placeholder={t(translations.entity_form.phone_number_placeholder)}
                    countryCode={mobileCountryCode}
                    phoneNumber={model.mobilePhoneNumber || ''}
                    handleChangeCountryCode={(value) => {
                      setMobileCountryCode(value);
                    }}
                    handleChangePhoneNumber={(value) =>
                      handleChangeTextField('mobilePhoneNumber', value)
                    }
                    disabled={
                      officeContext.state.entityFormDeleteButtonState ===
                        SubmitButtonState.Loading ||
                      officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                    }
                    handleBlur={() => setShowMobilePhoneNumberError(true)}
                    error={mobilePhoneNumberError}
                  />
                </>
              ) : (
                <>
                  <ReadOnlyTextField
                    label={t(translations.entity_form.landline_phone_number_input)}
                    value={
                      landlineInternationalPhoneNumber
                        ? landlineInternationalPhoneNumber
                        : originalModel?.landlinePhoneNumber ||
                          t(translations.entity_form.not_specified)
                    }
                    disabledTooltip={t(translations.entity_form.input_disabled_tooltip)}
                    startAdornment={() => {
                      if (landlineCountryCode && landlineInternationalPhoneNumber)
                        return (
                          <img
                            loading="lazy"
                            width="20"
                            {...getCountryFlagByCDN(landlineCountryCode.code)}
                            alt={`Country flag for phone extension ${landlineCountryCode.code}`}
                          />
                        );
                      return undefined;
                    }}
                  />
                  <ReadOnlyTextField
                    label={t(translations.entity_form.mobile_phone_number_input)}
                    value={
                      mobileInternationalPhoneNumber
                        ? mobileInternationalPhoneNumber
                        : originalModel?.mobilePhoneNumber ||
                          t(translations.entity_form.not_specified)
                    }
                    disabledTooltip={t(translations.entity_form.input_disabled_tooltip)}
                    startAdornment={() => {
                      if (mobileCountryCode && mobileInternationalPhoneNumber)
                        return (
                          <img
                            loading="lazy"
                            width="20"
                            {...getCountryFlagByCDN(mobileCountryCode.code)}
                            alt={`Country flag for phone extension ${mobileCountryCode.code}`}
                          />
                        );
                      return undefined;
                    }}
                  />
                </>
              )}
            </Box>

            <Box className="entity-form-even-cols">
              {props.canWrite ? (
                <FlatMultiTextSelect
                  label={t(translations.entity_form.social_responses_input)}
                  placeholder={t(translations.entity_form.social_responses_placeholder_input)}
                  // @ts-ignore
                  value={
                    model.socialResponses &&
                    entitySocialResponseOptions &&
                    entitySocialResponseOptions.length > 0
                      ? model.socialResponses.map((s) => {
                          const options = convertArrayToIndex<Option>(
                            entitySocialResponseOptions,
                            'key'
                          );

                          return {
                            key: s,
                            label: options[s]?.label ?? s,
                          };
                        })
                      : undefined
                  }
                  options={entitySocialResponseOptions ?? []}
                  onBlur={() => setShowSocialResponsesError(true)}
                  onChange={(selected) => {
                    setModel({
                      ...model,
                      socialResponses: Array.isArray(selected)
                        ? selected.map((s) => s.key)
                        : undefined,
                    });
                  }}
                  errors={socialResponsesError}
                  required
                />
              ) : (
                <ReadOnlyTextField
                  label={t(translations.entity_form.social_responses_input)}
                  value={
                    model.socialResponses &&
                    entitySocialResponseOptions &&
                    Object.entries(entitySocialResponseOptions).length > 0
                      ? model.socialResponses.map((s, index) => {
                          const options = convertArrayToIndex<Option>(
                            entitySocialResponseOptions,
                            'key'
                          );

                          return index === 0 ? options[s].label : ` ${options[s].label}`;
                        })
                      : t(translations.entity_form.not_specified)
                  }
                  disabledTooltip={t(translations.entity_form.input_disabled_tooltip)}
                />
              )}
            </Box>

            <Divider />

            <Box className="entity-form-even-cols">
              {props.canWrite && props.hasDIaccess ? (
                <>
                  <Box sx={{width: '100%'}}>
                    <FlatInput
                      label={t(translations.entity_form.services_name_input)}
                      value={model.servicesName || ''}
                      placeholder={t(translations.entity_form.services_name_placeholder_input)}
                      onChange={(e) => handleChangeTextField('servicesName', e.currentTarget.value)}
                      disabled={
                        officeContext.state.entityFormDeleteButtonState ===
                          SubmitButtonState.Loading ||
                        officeContext.state.entityFormSubmitButtonState ===
                          SubmitButtonState.Loading
                      }
                    />
                  </Box>
                  <Box sx={{width: '100%'}}>
                    <PhoneNumberInput
                      label={t(translations.entity_form.services_phone_number_input)}
                      placeholder={t(translations.entity_form.phone_number_placeholder)}
                      countryCode={servicesCountryCode}
                      phoneNumber={model.servicesPhoneNumber || ''}
                      handleChangeCountryCode={(value) => {
                        setServicesCountryCode(value);
                      }}
                      handleChangePhoneNumber={(value) =>
                        handleChangeTextField('servicesPhoneNumber', value)
                      }
                      disabled={
                        officeContext.state.entityFormDeleteButtonState ===
                          SubmitButtonState.Loading ||
                        officeContext.state.entityFormSubmitButtonState ===
                          SubmitButtonState.Loading
                      }
                      handleBlur={() => setShowServicesPhoneNumberError(true)}
                      error={servicesPhoneNumberError}
                    />
                  </Box>
                </>
              ) : (
                <>
                  <ReadOnlyTextField
                    label={t(translations.entity_form.services_name_input)}
                    value={model.servicesName || t(translations.entity_form.not_specified)}
                    disabledTooltip={
                      <Trans
                        values={{
                          module: t(translations.office.access['digital_inclusion']),
                        }}
                      >
                        {!props.hasDIaccess
                          ? t(translations.office.access.tooltip)
                          : t(translations.entity_form.input_disabled_tooltip)}
                      </Trans>
                    }
                  />
                  <ReadOnlyTextField
                    label={t(translations.entity_form.services_phone_number_input)}
                    value={
                      servicesInternationalPhoneNumber || t(translations.entity_form.not_specified)
                    }
                    disabledTooltip={
                      <Trans
                        values={{
                          module: t(translations.office.access['digital_inclusion']),
                        }}
                      >
                        {!props.hasDIaccess
                          ? t(translations.office.access.tooltip)
                          : t(translations.entity_form.input_disabled_tooltip)}
                      </Trans>
                    }
                    startAdornment={() => {
                      if (servicesCountryCode && servicesInternationalPhoneNumber)
                        return (
                          <img
                            loading="lazy"
                            width="20"
                            {...getCountryFlagByCDN(servicesCountryCode.code)}
                            alt={`Country flag for phone extension ${servicesCountryCode.code}`}
                          />
                        );
                      return undefined;
                    }}
                  />
                </>
              )}
            </Box>

            <Box className="entity-form-services-image">
              {props.canWrite && props.hasDIaccess ? (
                <EditPicture
                  label={t(translations.entity_form.services_image_url_input)}
                  type="entity"
                  className="entity-form-edit-picture"
                  alt={t(translations.entity_form.picture_input)}
                  src={model?.servicesImageUrl || undefined}
                  handleChange={handleChangeServicesImageFile}
                  disabled={
                    officeContext.state.entityFormDeleteButtonState === SubmitButtonState.Loading ||
                    officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                  }
                  aspect={appSizes.profileImageSize.width / appSizes.profileImageSize.height}
                  size={`${appSizes.profileImageSize.width}x${appSizes.profileImageSize.height}`}
                  alertMessage={t(translations.entity_form.picture_info_message)}
                  useFileCropper
                  clearable
                />
              ) : (
                <Box className="entity-form-picture">
                  <Tooltip.NotAllowedIf
                    condition={!props.hasDIaccess}
                    title={
                      <Trans
                        values={{
                          module: t(translations.office.access['digital_inclusion']),
                        }}
                      >
                        {t(translations.office.access.tooltip)}
                      </Trans>
                    }
                  >
                    <Avatar
                      alt={model.name}
                      src={model.servicesImageUrl}
                      sx={{
                        width: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                        height: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                      }}
                    >
                      <Business
                        sx={{
                          fontSize:
                            avatarSizesAndCoefficients.avatarSizes.extraGiant *
                            avatarSizesAndCoefficients.iconSizeCoefficient,
                        }}
                      />
                    </Avatar>
                  </Tooltip.NotAllowedIf>
                </Box>
              )}
            </Box>
          </Box>

          {(props.showExitButton || props.showPageTitle || props.canWrite) && (
            <Box className="entity-form-footer-wrapper">
              {props.showExitButton ? (
                <Button
                  variant="text"
                  color="error"
                  onClick={handleExit}
                  disabled={
                    !props.canWrite ||
                    officeContext.state.entityFormDeleteButtonState === SubmitButtonState.Loading ||
                    officeContext.state.entityFormSubmitButtonState === SubmitButtonState.Loading
                  }
                  sx={{
                    width: '100%',
                    height: '40px',
                    padding: '8px 16px',
                    borderRadius: '5px',
                    maxWidth: '252px',
                  }}
                >
                  {t(translations.entity_form.exit_button)}
                </Button>
              ) : undefined}

              <SubmitButton
                disabled={!props.canWrite || submitButtonDisabled}
                state={officeContext.state.entityFormSubmitButtonState || SubmitButtonState.Normal}
                onStateChange={(newState: SubmitButtonState) =>
                  officeContext.setEntityFormSubmitButtonState(newState)
                }
                normalStateLabel={t(translations.entity_form.save_button)}
                loadingStateLabel={t(translations.entity_form.saving_button)}
                successStateLabel={t(translations.entity_form.success.save_success_button)}
                errorStateLabel={t(translations.entity_form.errors.error_on_save_button)}
                endIcon={<Save />}
                sx={{
                  width: '100%',
                  height: '40px',
                  padding: '8px 16px',
                  borderRadius: '5px',
                  maxWidth: '252px',
                }}
                onClick={handleSaveChanges}
              />
            </Box>
          )}
        </form>
      ) : (
        <LoadingContentAbsolute />
      )}
      {/* Delete button and dialog */}
      {model && model.id && props.caregiverMode === CaregiverMode.SystemAdministrator && (
        <>
          <Box className="entity-form-delete-container">
            <Divider />
            <Box className="entity-form-delete-button-container">
              <SubmitButton
                disabled={deleteButtonDisabled}
                state={officeContext.state.entityFormDeleteButtonState || SubmitButtonState.Normal}
                onStateChange={(newState: SubmitButtonState) =>
                  officeContext.setEntityFormDeleteButtonState(newState)
                }
                normalStateLabel={t(translations.entity_form.delete_button)}
                loadingStateLabel={t(translations.entity_form.deleting_button)}
                successStateLabel={t(translations.entity_form.success.delete_success_button)}
                errorStateLabel={t(translations.entity_form.errors.error_on_delete_button)}
                endIcon={<Delete />}
                type="button"
                variant="outlined"
                color="error"
                onClick={(event) => {
                  event.preventDefault();
                  handleDeleteDialogOpened(true);
                }}
              />
            </Box>
          </Box>

          <AlertModal
            id="confirm-delete-entity"
            opened={deleteDialogOpened}
            title={t(translations.entity_form.delete_dialog_title, {
              name: originalModel?.shortName || originalModel?.name || '',
            })}
            content={t(translations.entity_form.delete_dialog_content)}
            handleClose={() => handleDeleteDialogOpened(false)}
            actions={[
              {
                label: t(translations.entity_form.delete_dialog_action_yes_button),
                type: 'destructive',
                handleClick: handleDeleteEntity,
              },
              {
                label: t(translations.entity_form.delete_dialog_action_no_button),
                type: 'neutral',
                handleClick: () => handleDeleteDialogOpened(false),
              },
            ]}
            preventCloseClick
          />
        </>
      )}
      {/* Router */}
      {model && props.canWrite && (
        <RouterPrompt
          // Show prompt if the data changed but make sure the path
          // is not changing because the entity was deleted/created
          when={
            dataChanged &&
            officeContext.state.entityFormDeleteButtonState !== SubmitButtonState.Loading &&
            officeContext.state.entityFormSubmitButtonState !== SubmitButtonState.Loading
          }
          subtitle={routerDialogTitle}
          message={routerDialogContent}
          onOK={() => true}
          onCancel={() => false}
          toastNotification={{
            severity: 'info',
            message: routerCancelToast,
          }}
        />
      )}
    </ContentPage>
  );
};
